import { Tabs } from '@wix/design-system';
import React, { useContext, useRef, useState, useMemo } from 'react';
import { RicosContext } from 'ricos-context';
import { dataHook, WEB_ADDRESS_CATEGORY, ADD_PLUGIN_LINK_BI, LINK_BUTTON_TYPE } from 'wix-rich-content-common';
import { unreachable } from 'wix-rich-content-editor-common';
import { SidePanelLayout, FooterActions } from 'wix-rich-content-ui-components';
import { s as styleInject } from '../style-inject.es-e7b07dd7.js';
import { DesignComponent } from '../components/design-component.js';
import { SettingsComponent } from '../components/settings-component.js';
import { SETTINGS_TAB } from '../constants.js';
import { ROOT, BUTTON_INPUT_MODAL, SETTINGS_TABS, SETTINGS_TAB_SETTINGS, SETTINGS_TAB_DESIGN } from '../dataHooks.js';
import '../utils/resolve-colors.js';
import '../defaults.js';
import 'ricos-content';
import 'ricos-schema';
import '../types.js';

var css_248z = "[dir] .N1Ijj{padding:18px 24px}.U-uTi{--wsr-color-B10:var(--rcp-color-primary)}.U-uTi button:hover{--wsr-color-B10:var(--rcp-color-hover)}";
var classes = {"content":"N1Ijj","tabs":"U-uTi"};
styleInject(css_248z);

const defaultBorderRadius = 0;
const defaultBorderWidth = 0;
const SettingsModalContent = ({
  buttonData,
  onChange,
  onSave,
  onCancel,
  config,
  helpers,
  nodeId,
  activeTab,
  onTabChange
}) => {
  const { t } = useContext(RicosContext);
  const inputRef = useRef(null);
  const [isValidUrl, setIsValidUrl] = useState(true);
  const { defaultBackgroundColor, defaultTextColor } = useMemo(() => {
    var _a, _b, _c, _d;
    const button = document.querySelector(dataHook(`editor-${nodeId}`, ROOT));
    const style = button ? window.getComputedStyle(button) : void 0;
    const defaultBackgroundColor2 = (_b = (_a = config.colors) == null ? void 0 : _a.color8) != null ? _b : (style == null ? void 0 : style.getPropertyValue("--ricos-action-color")) || "#116DFF";
    const defaultTextColor2 = (_d = (_c = config.colors) == null ? void 0 : _c.color1) != null ? _d : (style == null ? void 0 : style.getPropertyValue("--ricos-background-color")) || "#FFFFFF";
    return { defaultBackgroundColor: defaultBackgroundColor2, defaultTextColor: defaultTextColor2 };
  }, []);
  const handleOnSave = () => {
    var _a;
    if (!isValidUrl) {
      (_a = inputRef.current) == null ? void 0 : _a.focus();
      return;
    }
    triggerLinkBi();
    onSave();
  };
  const triggerLinkBi = () => {
    var _a, _b, _c, _d, _e;
    const params = {
      link: (_a = buttonData.link) == null ? void 0 : _a.url,
      newTab: ((_b = buttonData.link) == null ? void 0 : _b.target) === "BLANK",
      category: WEB_ADDRESS_CATEGORY,
      rel: ((_d = (_c = buttonData.link) == null ? void 0 : _c.rel) == null ? void 0 : _d.nofollow) && "nofollow"
    };
    (_e = helpers == null ? void 0 : helpers.onPluginAction) == null ? void 0 : _e.call(helpers, ADD_PLUGIN_LINK_BI, {
      plugin_id: LINK_BUTTON_TYPE,
      params
    });
  };
  const resolveButtonStyles = (componentData) => {
    var _a, _b, _c, _d, _e, _f, _g;
    return {
      border: {
        width: (_b = (_a = componentData == null ? void 0 : componentData.border) == null ? void 0 : _a.width) != null ? _b : defaultBorderWidth,
        radius: (_d = (_c = componentData == null ? void 0 : componentData.border) == null ? void 0 : _c.radius) != null ? _d : defaultBorderRadius
      },
      colors: {
        text: (_e = componentData == null ? void 0 : componentData.colors) == null ? void 0 : _e.text,
        border: (_f = componentData == null ? void 0 : componentData.colors) == null ? void 0 : _f.border,
        background: (_g = componentData == null ? void 0 : componentData.colors) == null ? void 0 : _g.background
      }
    };
  };
  const resolveStylesChange = ({ colors, border }) => {
    return {
      colors,
      border: {
        width: border.width === defaultBorderWidth ? void 0 : border.width,
        radius: border.radius === defaultBorderRadius ? void 0 : border.radius
      }
    };
  };
  return /* @__PURE__ */ React.createElement(
    SidePanelLayout,
    {
      onCloseButtonClick: onCancel,
      dataHook: BUTTON_INPUT_MODAL,
      header: { title: t("ButtonModal_Header") },
      content: {
        noPadding: true,
        children: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
          Tabs,
          {
            className: classes.tabs,
            dataHook: SETTINGS_TABS,
            hasDivider: true,
            minWidth: "100%",
            alignment: "center",
            type: "uniformFull",
            onClick: (tab) => onTabChange(tab.id),
            activeId: activeTab,
            items: [
              {
                id: SETTINGS_TAB.SETTINGS,
                title: t("ButtonModal_Settings_Tab"),
                dataHook: SETTINGS_TAB_SETTINGS
              },
              {
                id: SETTINGS_TAB.DESIGN,
                title: t("ButtonModal_Design_Tab"),
                dataHook: SETTINGS_TAB_DESIGN
              }
            ]
          }
        ), /* @__PURE__ */ React.createElement("div", { className: classes.content }, (() => {
          switch (activeTab) {
            case SETTINGS_TAB.SETTINGS:
              return /* @__PURE__ */ React.createElement(
                SettingsComponent,
                {
                  buttonData,
                  onChange,
                  inputRef,
                  setIsValidUrl
                }
              );
            case SETTINGS_TAB.DESIGN:
              return /* @__PURE__ */ React.createElement(
                DesignComponent,
                {
                  stylesData: resolveButtonStyles(buttonData.styles),
                  onChange: (newData) => {
                    const styles = resolveStylesChange(newData);
                    onChange({ styles });
                  },
                  config,
                  defaultBackgroundColor,
                  defaultTextColor
                }
              );
            default:
              unreachable(activeTab);
              return null;
          }
        })()))
      },
      footer: { children: /* @__PURE__ */ React.createElement(FooterActions, { onSave: handleOnSave, onCancel }) }
    }
  );
};

export { SettingsModalContent };
//# sourceMappingURL=SettingsModalContent.js.map
